'use client';

import { useState } from 'react';
import { usePreviousDistinct } from 'react-use';

import { Container } from '@/components/Container';
import { useActionHandler } from '@/hooks/useAction';
import * as Field from '@/ui/Field';
import * as Modal from '@/ui/Modal';

import { ShareCopyButton } from './ShareCopyButton';
import { SocialButton } from './SocialButton';

export function ShareModal() {
    const [url, setUrl] = useState<string>();

    const prevUrl = usePreviousDistinct(url);

    useActionHandler<string>('open_share_modal', (input) => {
        setUrl(input || global.location.href);
    });

    function handleOpenChange(open: boolean) {
        if (!open) {
            setUrl(undefined);
        }
    }

    return (
        <Modal.Root
            className="w-full max-w-2xl"
            id="share-modal"
            open={!!url}
            onOpenChange={handleOpenChange}
        >
            <Container className="py-sm md:py-md lg:py-lg">
                <header className="mb-md flex items-center">
                    <Modal.Title>Share</Modal.Title>
                    <Modal.Description hidden />

                    <div className="ml-auto flex pl-sm">
                        <Modal.CloseButton />
                    </div>
                </header>

                <div className="mb-md grid items-center gap-sm sm:grid-cols-3">
                    <SocialButton
                        className="text-[#0866ff] hover:text-white"
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url!)}`}
                        aria-label="Facebook"
                    >
                        <svg
                            className="size-6 fill-current"
                            viewBox="0 0 36 36"
                        >
                            <path d="M20.181 35.87C29.094 34.791 36 27.202 36 18c0-9.941-8.059-18-18-18S0 8.059 0 18c0 8.442 5.811 15.526 13.652 17.471L14 34h5.5l.681 1.87Z" />
                            <path
                                className="fill-white transition group-hover/button:fill-gray"
                                d="M13.651 35.471v-11.97H9.936V18h3.715v-2.37c0-6.127 2.772-8.964 8.784-8.964 1.138 0 3.103.223 3.91.446v4.983c-.425-.043-1.167-.065-2.081-.065-2.952 0-4.09 1.116-4.09 4.025V18h5.883l-1.008 5.5h-4.867v12.37a18.183 18.183 0 0 1-6.53-.399Z"
                            />
                        </svg>
                        <span>Facebook</span>
                    </SocialButton>
                    <SocialButton
                        className="text-[#000000]"
                        href={`https://x.com/intent/tweet?url=${encodeURIComponent(url!)}`}
                        aria-label="X"
                    >
                        <svg
                            className="size-6 fill-current"
                            viewBox="0 0 300 300.251"
                        >
                            <path d="M178.57 127.15 290.27 0h-26.46l-97.03 110.38L89.34 0H0l117.13 166.93L0 300.25h26.46l102.4-116.59 81.8 116.59h89.34M36.01 19.54H76.66l187.13 262.13h-40.66" />
                        </svg>
                    </SocialButton>
                    <SocialButton
                        className="text-[#0a66c2]"
                        href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url!)}`}
                        aria-label="LinkedIn"
                    >
                        <svg
                            className="size-6 fill-current"
                            viewBox="0 0 24 24"
                        >
                            <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z" />
                        </svg>
                        <span>LinkedIn</span>
                    </SocialButton>
                </div>

                <Field.Root>
                    <Field.Label>Copy URL</Field.Label>
                    <Field.Input
                        type="url"
                        value={url || prevUrl || ''}
                        readOnly
                        className="pr-2"
                        afterAction={<ShareCopyButton url={url!} />}
                    />
                </Field.Root>
            </Container>
        </Modal.Root>
    );
}
